import { useCallback, useRef, useEffect } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { AppLayout } from './AppLayout'
import { NavigationTour } from '@/components/onboarding'
import { useAuthContext } from '@/components/auth/AuthProvider'
import { useAuth, useUnreadCount, useRealtimeNotifications, useToast, useTranslation } from '@/hooks'
import { useUnreadInboxCount } from '@/hooks/useInbox'
import { queryKeys } from '@/hooks/useQueryKeys'
import type { InboxMessage } from '@/types'

const DEBOUNCE_MS = 300

/**
 * ProtectedLayoutWrapper Component
 * Wraps protected pages with AppLayout and provides authentication context.
 *
 * Features:
 * - Fetches user profile from auth context
 * - Fetches unread notification count
 * - Fetches unread inbox count for bell badge
 * - Subscribes to realtime notifications for toasts
 * - Provides signOut functionality to AppLayout
 * - Renders child routes via React Router's Outlet
 */
export function ProtectedLayoutWrapper() {
  const { profile, user } = useAuthContext()
  const { signOut } = useAuth()
  const { t } = useTranslation()
  const { show: toast, dismiss } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const userId = user?.id ?? null

  const { refetch } = useUnreadCount()
  const { count: inboxCount, refetch: refetchInbox } = useUnreadInboxCount()

  // Debounce refs for batching realtime notifications
  const messageBufferRef = useRef<InboxMessage[]>([])
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const currentNotificationToastIdRef = useRef<string | null>(null)

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [])

  // Flush the message buffer: show one toast for the batch, refetch once
  const flushNotificationBuffer = useCallback(() => {
    const messages = messageBufferRef.current
    if (messages.length === 0) return
    messageBufferRef.current = []

    // Dismiss any existing notification toast
    if (currentNotificationToastIdRef.current) {
      dismiss(currentNotificationToastIdRef.current)
      currentNotificationToastIdRef.current = null
    }

    let toastId: string
    if (messages.length === 1) {
      const msg = messages[0]
      toastId = toast(
        msg.message_type === 'system' ? 'system' : 'alert',
        msg.title || t('notifications.newNotification', { defaultValue: 'Ново известие' }),
        msg.body_preview || t('notifications.checkNotifications', { defaultValue: 'Имате ново съвпадение с правило' }),
        7000,
        {
          actionText: t('notifications.viewInInbox'),
          actionOnClick: () => navigate(`/inbox?id=${msg.id}`),
        },
      )
    } else {
      toastId = toast(
        'alert',
        t('notifications.newNotificationsCount', { count: messages.length, defaultValue: '{{count}} нови известия' }),
        t('notifications.checkNotificationsBatch', { defaultValue: 'Проверете входящата поща за детайли' }),
        7000,
        {
          actionText: t('notifications.openInbox'),
          actionOnClick: () => navigate('/inbox'),
        },
      )
    }
    currentNotificationToastIdRef.current = toastId

    // Refetch counts once per batch
    refetch()
    refetchInbox()
    queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all(userId) })
    queryClient.invalidateQueries({ queryKey: queryKeys.inbox.all(userId) })
  }, [toast, dismiss, t, navigate, refetch, refetchInbox, queryClient, userId])

  // Handle new realtime inbox messages with debounce batching
  const handleNewNotification = useCallback(
    (message: InboxMessage) => {
      messageBufferRef.current.push(message)

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = setTimeout(flushNotificationBuffer, DEBOUNCE_MS)
    },
    [flushNotificationBuffer]
  )

  // Subscribe to realtime notifications
  useRealtimeNotifications({
    onNewNotification: handleNewNotification,
    enabled: true,
  })

  const handleSignOut = useCallback(async () => {
    console.log('[ProtectedLayoutWrapper] handleSignOut called')

    // Clear ALL queries before signing out to prevent stale data
    queryClient.clear()
    console.log('[ProtectedLayoutWrapper] Cache cleared before signOut')

    // Helper to force clear session from localStorage
    const forceLogout = () => {
      console.log('[ProtectedLayoutWrapper] Force clearing session from localStorage')
      // Clear all Supabase auth keys
      const keysToRemove = Object.keys(localStorage).filter(
        (key) => key.startsWith('sb-') && key.includes('-auth-token')
      )
      keysToRemove.forEach((key) => localStorage.removeItem(key))
    }

    try {
      // Add timeout to detect if signOut is hanging
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('signOut timed out after 5s')), 5000)
      )

      const result = await Promise.race([signOut(), timeoutPromise])
      console.log('[ProtectedLayoutWrapper] signOut result:', result)
      navigate('/login')
    } catch (error) {
      console.error('[ProtectedLayoutWrapper] signOut error:', error)
      // Force clear session if signOut failed/timed out
      forceLogout()
      // Even if signOut fails, redirect to login
      navigate('/login')
    }
  }, [signOut, navigate, queryClient])

  return (
    <>
      <AppLayout
        userName={profile?.full_name ?? undefined}
        userEmail={profile?.email}
        inboxCount={inboxCount ?? 0}
        onSignOut={handleSignOut}
      >
        <Outlet />
      </AppLayout>
      <NavigationTour />
    </>
  )
}
